import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { stockService } from "../../services/stockService";
import { productService } from "../../services/productService";
import { Stock, StockMovement, StockInfo } from "../../types";

// Define the initial state interface
interface StockState {
    stockItems: Stock[];
    stockMovements: StockMovement[];
    selectedStock: Stock | null;
    isLoading: boolean;
    isAdjusting: boolean;
    error: string | null;
    lowStockItems: Stock[];
}

// Define the initial state
const initialState: StockState = {
    stockItems: [],
    stockMovements: [],
    selectedStock: null,
    isLoading: false,
    isAdjusting: false,
    error: null,
    lowStockItems: [],
};

// Async thunks
export const fetchAllStock = createAsyncThunk(
    "stock/fetchAllStock",
    async (_, { rejectWithValue }) => {
        try {
            // Note: This would need to be implemented in the backend
            // For now, return empty array
            const stockItems: Stock[] = [];
            return stockItems;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch stock items",
            );
        }
    },
);

export const fetchProductStock = createAsyncThunk(
    "stock/fetchProductStock",
    async (productId: string, { rejectWithValue }) => {
        try {
            // Use optimized products endpoint that includes stock data
            const response = await productService.getProducts({
                search: productId,
                limit: 1,
                include_stock: true,
            });

            const product = response.items[0];

            if (!product || !product.stock) {
                throw new Error("Product or stock data not found");
            }

            // Convert product stock to StockInfo format
            const stockInfo: StockInfo = {
                quantity: product.stock.quantity,
                reserved_quantity: product.stock.reserved_quantity,
                min_stock_level: product.stock.min_stock_level || 10,
                max_stock_level: product.stock.max_stock_level || 1000,
                last_updated: new Date().toISOString(),
            };

            return stockInfo;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch product stock",
            );
        }
    },
);

export const adjustStock = createAsyncThunk(
    "stock/adjustStock",
    async (
        {
            productId,
            newQuantity,
            reason,
        }: { productId: string; newQuantity: number; reason?: string },
        { rejectWithValue, dispatch },
    ) => {
        try {
            await stockService.adjustStock(productId, newQuantity, reason);
            // Refresh stock data
            dispatch(fetchAllStock());
            return { productId, newQuantity, reason };
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to adjust stock");
        }
    },
);

export const fetchStockMovements = createAsyncThunk(
    "stock/fetchStockMovements",
    async (productId: string, { rejectWithValue }) => {
        try {
            const movements = await stockService.getStockMovements(productId);
            return movements;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch stock movements",
            );
        }
    },
);

export const fetchLowStockItems = createAsyncThunk(
    "stock/fetchLowStockItems",
    async (_, { rejectWithValue }) => {
        try {
            const lowStockItems = await stockService.getLowStockReport();
            // Convert to Stock format for compatibility
            const stockItems: Stock[] = lowStockItems.map((item) => ({
                stock_id: item.product_id,
                product_id: item.product_id,
                available_quantity: item.available_quantity,
                reserved_quantity: item.reserved_quantity,
                reorder_level: item.min_stock_level,
                max_stock_level: item.max_stock_level,
                location: "",
                is_low_stock: item.is_low_stock,
                create_time: new Date().toISOString(),
                update_time: new Date().toISOString(),
            }));
            return stockItems;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch low stock items",
            );
        }
    },
);

// Create the stock slice
const stockSlice = createSlice({
    name: "stock",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setSelectedStock: (state, action: PayloadAction<Stock | null>) => {
            state.selectedStock = action.payload;
        },
        clearSelectedStock: (state) => {
            state.selectedStock = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch all stock
            .addCase(fetchAllStock.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAllStock.fulfilled, (state, action) => {
                state.isLoading = false;
                state.stockItems = action.payload;
                state.error = null;
            })
            .addCase(fetchAllStock.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch product stock
            .addCase(fetchProductStock.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchProductStock.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(fetchProductStock.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Adjust stock
            .addCase(adjustStock.pending, (state) => {
                state.isAdjusting = true;
                state.error = null;
            })
            .addCase(adjustStock.fulfilled, (state) => {
                state.isAdjusting = false;
                state.error = null;
            })
            .addCase(adjustStock.rejected, (state, action) => {
                state.isAdjusting = false;
                state.error = action.payload as string;
            })
            // Fetch stock movements
            .addCase(fetchStockMovements.fulfilled, (state, action) => {
                // Map the service response to match our StockMovement type
                state.stockMovements = action.payload.map((movement) => ({
                    movement_id: movement.movement_id,
                    stock_id: movement.stock_id,
                    reason: movement.movement_type as
                        | "PURCHASE"
                        | "SALE"
                        | "ADJUSTMENT"
                        | "RETURN"
                        | "DAMAGE"
                        | "EXPIRED",
                    quantity: movement.quantity,
                    reference_type: movement.reference_type,
                    reference_id: movement.reference_id,
                    notes: movement.notes,
                    create_time: movement.create_time,
                    update_time: movement.create_time, // Use create_time as fallback
                }));
            })
            // Fetch low stock items
            .addCase(fetchLowStockItems.fulfilled, (state, action) => {
                state.lowStockItems = action.payload;
            });
    },
});

// Export actions
export const { clearError, setSelectedStock, clearSelectedStock } =
    stockSlice.actions;

// Export selectors
export const selectStockItems = (state: any) => state.stock.stockItems;
export const selectStockMovements = (state: any) => state.stock.stockMovements;
export const selectSelectedStock = (state: any) => state.stock.selectedStock;
export const selectStockLoading = (state: any) => state.stock.isLoading;
export const selectStockAdjusting = (state: any) => state.stock.isAdjusting;
export const selectStockError = (state: any) => state.stock.error;
export const selectLowStockItems = (state: any) => state.stock.lowStockItems;

// Export reducer
export default stockSlice.reducer;
